// 账号/同步 API 客户端(同域,带 cookie)。
export interface Socials {
  x?: string;
  instagram?: string;
  facebook?: string;
  tiktok?: string;
  youtube?: string;
  github?: string;
}
export interface Me {
  uid: string | null;
  authed: boolean;
  providers?: { google: boolean; discord: boolean };
  displayName?: string | null;
  avatar?: string | null;
  xp?: number;
  level?: number;
  floor?: number;
  next?: number | null;
  streak?: { current: number; best: number };
  connStreak?: { current: number; best: number };
  endlessBest?: number;
  modeStats?: Record<string, { streak: number; best: number; endlessBest: number }>;
  bio?: string | null;
  birthday?: string | null;
  country?: string | null;
  gender?: string | null;
  socials?: Socials;
  medals?: MedalCounts;
  held?: HeldBadge[];
  today?: Record<string, { solved: boolean; guesses: number }>;
  day?: string;
}

export interface ProfileInput {
  displayName?: string | null;
  bio?: string | null;
  birthday?: string | null;
  country?: string | null;
  gender?: string | null;
  socials?: Socials;
}

export interface SyncPayload {
  day: string;
  localStreak: number;
  endlessBests: Record<string, number>; // 各模式无尽历史最佳 {classic,flag,outline,connections}
  results: { mode: string; solved: boolean; guesses: number }[];
}

const opts: RequestInit = { credentials: 'include' };

export async function fetchMe(): Promise<Me> {
  const r = await fetch('/api/me', opts);
  if (!r.ok) throw new Error('me failed');
  return r.json();
}

export async function syncProgress(payload: SyncPayload): Promise<Me> {
  const r = await fetch('/api/sync', {
    ...opts,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error('sync failed');
  return r.json();
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { ...opts, method: 'POST' });
}

export const loginUrl = (provider: 'google' | 'discord'): string => `/api/auth/${provider}`;

// ===== 竞技 Classic Daily =====
/** 数值列线索。unknown = 任一方无数据 */
export interface ServerNumClue {
  hit: boolean;
  higher: boolean;
  unknown?: boolean;
}
export interface ServerClue {
  hit: boolean;
  region: boolean;
  population: ServerNumClue;
  area: ServerNumClue;
  borders: ServerNumClue;
  gdpPc: ServerNumClue;
  temp: ServerNumClue;
  distanceKm: number;
  bearing: number;
  distanceColor: string;
}
export interface CompGame {
  day: string;
  status: 'playing' | 'won';
  guesses: { cca3: string; clue: ServerClue }[];
  answerCca3: string | null;
}
export interface GuessResp {
  status: 'playing' | 'won';
  guessNo: number;
  guess?: { cca3: string; clue: ServerClue };
  answerCca3: string | null;
  xp?: { gained: number; level: number; leveledUp: boolean } | null;
}

export async function getGame(day: string): Promise<CompGame> {
  const r = await fetch(`/api/guess?day=${day}`, opts);
  if (!r.ok) throw new Error('game ' + r.status);
  return r.json();
}

export async function postGuess(day: string, cca3: string): Promise<GuessResp> {
  const r = await fetch('/api/guess', {
    ...opts,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ day, cca3 }),
  });
  if (!r.ok) throw new Error('guess ' + r.status);
  return r.json();
}

export async function reveal(day: string): Promise<{ day: string; cca3: string }> {
  const r = await fetch(`/api/reveal?day=${day}`, opts);
  if (!r.ok) throw new Error('reveal ' + r.status);
  return r.json();
}

// ===== P4 排行榜 / 统计 / 公开页 / 删号 =====
/** 生涯每日奖牌计数(仅 Today 综合榜产生) */
export interface MedalCounts {
  gold: number;
  silver: number;
  bronze: number;
}
/** 榜单持有徽章:按 (board, rank) 分账记霸榜天数 */
export interface HeldBadge {
  board: 'streak' | 'unlimited' | 'level';
  rank: 1 | 2 | 3;
  days: number;
}

export interface LbRow {
  id: string;
  name: string | null;
  avatar: string | null;
  level: number;
  value: number;
  rank: number;
  isMe: boolean;
  medals: MedalCounts;
  /** 全部榜单的持有徽章,不只当前榜 */
  held: HeldBadge[];
  /** 仅 Today 综合榜:该用户今日已完成的模式 */
  /** Today 榜:当日已完成的模式 + 各自的原始 guesses(语义按模式不同)*/
  modes?: { mode: LbMode; guesses: number }[];
  /** Today 榜的并列裁决键:各模式归一化失分之和,越小越好 */
  norm_loss?: number;
  completed?: number;
}
export type Board = 'today' | 'streak' | 'unlimited' | 'level';
export type LbMode = 'classic' | 'flag' | 'outline' | 'connections' | 'draw' | 'trivia';
export interface Leaderboard {
  board: Board;
  mode: LbMode;
  top: LbRow[];
  me: { rank: number; value: number } | null;
}
export interface Stats {
  xp: number;
  level: number;
  streak: { current: number; best: number };
  connStreak: { current: number; best: number };
  streaks: Record<string, { current: number; best: number }>;
  modes: { mode: string; total: number; solved: number }[];
  guessDist: { guesses: number; n: number }[];
}
export interface PublicProfile {
  id: string;
  displayName: string | null;
  avatar: string | null;
  level: number;
  xp: number;
  streak: { current: number; best: number };
  connStreak: { current: number; best: number };
  endlessBest: number;
  modeStats?: Record<string, { streak: number; best: number; endlessBest: number }>;
  bio: string | null;
  birthday: string | null;
  country: string | null;
  gender: string | null;
  socials: Socials;
  modes: { mode: string; solved: number }[];
  medals: MedalCounts;
  held: HeldBadge[];
}

export async function getLeaderboard(board: string, mode = 'classic'): Promise<Leaderboard> {
  const r = await fetch(`/api/leaderboard?board=${board}&mode=${mode}`, opts);
  if (!r.ok) throw new Error('lb ' + r.status);
  return r.json();
}
export async function getStats(): Promise<Stats> {
  const r = await fetch('/api/stats', opts);
  if (!r.ok) throw new Error('stats ' + r.status);
  return r.json();
}
export async function getPublicProfile(id: string): Promise<PublicProfile> {
  const r = await fetch(`/api/u/${id}`, opts);
  if (!r.ok) throw new Error('profile ' + r.status);
  return r.json();
}
export async function deleteAccount(): Promise<void> {
  await fetch('/api/account', { ...opts, method: 'DELETE' });
}

// ===== 竞技 Connections Daily =====
export interface ConnGroupApi {
  catId: string;
  tier: number;
  members: string[];
}
export interface ConnGameApi {
  day: string;
  status: 'playing' | 'won' | 'lost';
  tiles: string[];
  locked: ConnGroupApi[];
  mistakes: number;
  submissions: string[][];
}
export interface ConnGuessResp {
  result?: 'correct' | 'oneAway' | 'wrong';
  status: 'playing' | 'won' | 'lost';
  mistakes: number;
  group?: ConnGroupApi | null;
  locked: ConnGroupApi[];
}

export async function getConnGame(day: string): Promise<ConnGameApi> {
  const r = await fetch(`/api/connections/guess?day=${day}`, opts);
  if (!r.ok) throw new Error('conn ' + r.status);
  return r.json();
}
export async function postConnGuess(day: string, picks: string[]): Promise<ConnGuessResp> {
  const r = await fetch('/api/connections/guess', {
    ...opts,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ day, picks }),
  });
  if (!r.ok) throw new Error('conn-guess ' + r.status);
  return r.json();
}
export async function connReveal(day: string): Promise<{ day: string; groups: ConnGroupApi[] }> {
  const r = await fetch(`/api/connections/reveal?day=${day}`, opts);
  if (!r.ok) throw new Error('conn-reveal ' + r.status);
  return r.json();
}

export async function updateProfile(input: ProfileInput): Promise<Me> {
  const r = await fetch('/api/profile', {
    ...opts,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error('profile ' + r.status);
  return r.json();
}

export async function uploadAvatar(webp: Blob): Promise<{ avatar: string }> {
  const r = await fetch('/api/avatar', {
    ...opts,
    method: 'POST',
    headers: { 'Content-Type': 'image/webp' },
    body: webp,
  });
  if (!r.ok) throw new Error('avatar ' + r.status);
  return r.json();
}

// ── Trivia ──────────────────────────────────────────────────────────────────
export interface TriviaChip {
  cca2: string;
  name: string;
}
export interface TriviaReveal {
  correctIndex: number;
  /** 大字主体 —— 答案本身(首都名 / 语言 / 人名 / 邻国数…) */
  lead: string;
  flag?: string;
  sub?: string;
  /** 国旗排的标题。没有标题的国旗排等于没说 */
  chipsLabel?: string;
  chips?: TriviaChip[];
  more?: number;
  bars?: { name: string; value: number; unit?: string }[];
}

export interface TriviaQ {
  i: number;
  id: string;
  type: string;
  difficulty: number;
  stem: string;
  options: string[];
  /** 只有答过的题才有 —— 未答的题里连揭晓卡片都不下发 */
  answered?: { chosen: number; reveal: TriviaReveal };
}
export interface TriviaData {
  day: string;
  puzzle: number;
  size: number;
  questions: TriviaQ[];
  score: number;
  done: boolean;
  answered: number;
  xp: number;
}
export interface TriviaAnswer {
  i: number;
  correct: boolean;
  reveal: TriviaReveal;
  score: number;
  done: boolean;
  xp: number;
}

export async function fetchTrivia(lang: string): Promise<TriviaData | null> {
  const r = await fetch(`/api/trivia?lang=${lang}`, opts);
  return r.ok ? r.json() : null;
}

export async function answerTrivia(i: number, chosen: number, lang: string): Promise<TriviaAnswer | { error: string }> {
  const r = await fetch('/api/trivia', {
    ...opts,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ i, chosen, lang }),
  });
  return r.json();
}
